// Catalog Shell - Event Emitter Utility
// ============================================================================

export type EventCallback<T = unknown> = (data: T) => void;

export class EventEmitter<TEvents extends string = string> {
    private listeners: Map<TEvents, Set<EventCallback>> = new Map();

    on<T = unknown>(event: TEvents, callback: EventCallback<T>): () => void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback as EventCallback);

        // Return unsubscribe function
        return () => this.off(event, callback);
    }

    off<T = unknown>(event: TEvents, callback: EventCallback<T>): void {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.delete(callback as EventCallback);
        }
    }

    emit<T = unknown>(event: TEvents, data?: T): void {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(cb => {
                try {
                    cb(data);
                } catch (err) {
                    console.error(`Error in event handler for '${event}':`, err);
                }
            });
        }
    }

    once<T = unknown>(event: TEvents, callback: EventCallback<T>): () => void {
        const wrapper: EventCallback<T> = (data) => {
            this.off(event, wrapper);
            callback(data);
        };
        return this.on(event, wrapper);
    }

    removeAllListeners(event?: TEvents): void {
        if (event) {
            this.listeners.delete(event);
        } else {
            this.listeners.clear();
        }
    }

    listenerCount(event: TEvents): number {
        return this.listeners.get(event)?.size ?? 0;
    }
}
